import React, { useState, useEffect } from 'react';
import { 
  X, 
  Crown, 
  Calendar, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  RefreshCw,
  BarChart3,
  Palette,
  Headphones,
  CalendarCheck,
  MapPin,
  Users,
  Image,
  Package,
  Check
} from 'lucide-react';

const SubscriptionDetailsModal = ({ isOpen, onClose, subscriptionDetails, onRefresh }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  // Calculate countdown timer
  const calculateTimeLeft = () => {
    if (!subscriptionDetails?.end_date) return null;
    
    const endDate = new Date(subscriptionDetails.end_date);
    const now = new Date();
    const difference = endDate - now;
    
    if (difference > 0) {
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      return { days, hours, minutes, seconds };
    }
    
    return null;
  };

  // Update countdown every second
  useEffect(() => {
    if (isOpen && subscriptionDetails?.end_date) {
      const updateTimer = () => {
        setTimeLeft(calculateTimeLeft());
      };
      
      updateTimer(); // Initial calculation
      const interval = setInterval(updateTimer, 1000); // Update every second
      
      return () => clearInterval(interval);
    }
  }, [isOpen, subscriptionDetails]);

  if (!isOpen || !subscriptionDetails) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'trial':
        return 'text-blue-600 bg-blue-100';
      case 'expired':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4" />;
      case 'trial':
        return <Clock className="h-4 w-4" />;
      case 'expired':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const isExpiringSoon = () => {
    if (!subscriptionDetails?.days_remaining) return false;
    return subscriptionDetails.days_remaining <= 5 && subscriptionDetails.days_remaining > 0;
  };

  const getUrgencyLevel = () => {
    if (!subscriptionDetails?.days_remaining) return null;
    
    if (subscriptionDetails.days_remaining <= 1) return 'critical';
    if (subscriptionDetails.days_remaining <= 3) return 'high';
    if (subscriptionDetails.days_remaining <= 5) return 'medium';
    return null;
  };

  const PLAN_FEATURES = [
    { key: 'feature_analytics', label: 'Advanced Analytics', icon: BarChart3 },
    { key: 'feature_custom_branding', label: 'Custom Branding', icon: Palette },
    { key: 'feature_priority_support', label: 'Priority Support', icon: Headphones },
    { key: 'feature_advanced_booking', label: 'Advanced Booking', icon: CalendarCheck },
    { key: 'feature_multi_location', label: 'Multi-Location', icon: MapPin },
  ];

  const features = subscriptionDetails?.features || {};
  const featuresList = subscriptionDetails?.features_list || [];
  const limits = subscriptionDetails?.limits || {};
  const usage = subscriptionDetails?.usage || {};

  const formatLimit = (value) => value == null ? 'Unlimited' : value;

  return (
    <div className="fixed inset-0 z-[10001] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
          {/* Header */}
          <div className="bg-white px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Crown className="h-6 w-6 text-primary-600" />
                <h3 className="text-xl font-semibold text-gray-900">Subscription Details</h3>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={onRefresh}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="h-5 w-5" />
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-4 sm:px-6 py-6">
            <div className="space-y-6">
              {/* Current Plan */}
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Current Plan</h4>
                <p className="text-2xl sm:text-3xl font-bold text-primary-600 mb-3">
                  {subscriptionDetails.plan_name}
                </p>
                <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(subscriptionDetails.status)}`}>
                  {getStatusIcon(subscriptionDetails.status)}
                  <span className="capitalize">{subscriptionDetails.status}</span>
                </div>
              </div>

              {/* Time Remaining */}
              <div className={`rounded-lg p-4 sm:p-6 ${
                isExpiringSoon() 
                  ? getUrgencyLevel() === 'critical' 
                    ? 'bg-red-50 border border-red-200' 
                    : getUrgencyLevel() === 'high'
                    ? 'bg-orange-50 border border-orange-200'
                    : 'bg-yellow-50 border border-yellow-200'
                  : 'bg-gray-50'
              }`}>
                <h4 className={`font-medium mb-4 text-sm sm:text-base ${
                  isExpiringSoon() 
                    ? getUrgencyLevel() === 'critical' 
                      ? 'text-red-900' 
                      : getUrgencyLevel() === 'high'
                      ? 'text-orange-900'
                      : 'text-yellow-900'
                    : 'text-gray-900'
                }`}>
                  Time Remaining
                  {isExpiringSoon() && (
                    <span className="ml-2 text-xs animate-pulse">
                      {getUrgencyLevel() === 'critical' ? '🚨' : getUrgencyLevel() === 'high' ? '⚠️' : '⏰'}
                    </span>
                  )}
                </h4>
                {timeLeft ? (
                  <div className="grid grid-cols-4 gap-3 sm:gap-4">
                    <div className="text-center">
                      <div className={`text-2xl sm:text-3xl font-bold ${
                        isExpiringSoon() 
                          ? getUrgencyLevel() === 'critical' 
                            ? 'text-red-600' 
                            : getUrgencyLevel() === 'high'
                            ? 'text-orange-600'
                            : 'text-yellow-600'
                          : 'text-gray-900'
                      }`}>{timeLeft.days}</div>
                      <div className="text-xs text-gray-600 mt-1">Days</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl sm:text-3xl font-bold ${
                        isExpiringSoon() 
                          ? getUrgencyLevel() === 'critical' 
                            ? 'text-red-600' 
                            : getUrgencyLevel() === 'high'
                            ? 'text-orange-600'
                            : 'text-yellow-600'
                          : 'text-gray-900'
                      }`}>{timeLeft.hours}</div>
                      <div className="text-xs text-gray-600 mt-1">Hours</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl sm:text-3xl font-bold ${
                        isExpiringSoon() 
                          ? getUrgencyLevel() === 'critical' 
                            ? 'text-red-600' 
                            : getUrgencyLevel() === 'high'
                            ? 'text-orange-600'
                            : 'text-yellow-600'
                          : 'text-gray-900'
                      }`}>{timeLeft.minutes}</div>
                      <div className="text-xs text-gray-600 mt-1">Min</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl sm:text-3xl font-bold ${
                        isExpiringSoon() 
                          ? getUrgencyLevel() === 'critical' 
                            ? 'text-red-600' 
                            : getUrgencyLevel() === 'high'
                            ? 'text-orange-600'
                            : 'text-yellow-600'
                          : 'text-gray-900'
                      }`}>{timeLeft.seconds}</div>
                      <div className="text-xs text-gray-600 mt-1">Sec</div>
                    </div>
                  </div>
                ) : (
                  <p className="text-lg text-red-600 font-medium">Expired</p>
                )}
              </div>

              {/* Plan Limits & Usage */}
              {(limits && Object.keys(limits).length > 0) && (
                <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Plan Limits</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-700">Bookings this month</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {usage.bookings_this_month ?? 0} / {formatLimit(limits.max_bookings_per_month)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Package className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-700">Services</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {usage.services_count ?? 0} / {formatLimit(limits.max_services)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Image className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-700">Portfolio items</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {usage.portfolio_count ?? 0} / {formatLimit(limits.max_portfolio_items)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan Features - from DB features_list, fallback to boolean feature flags */}
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Plan Features</h4>
                <div className="space-y-2">
                  {featuresList.length > 0 ? (
                    featuresList.map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-sm text-gray-900">{item}</span>
                      </div>
                    ))
                  ) : (
                    PLAN_FEATURES.map((feature) => {
                      const Icon = feature.icon;
                      const included = !!features[feature.key];
                      return (
                        <div key={feature.key} className="flex items-center space-x-3">
                          {included ? (
                            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                          ) : (
                            <span className="h-4 w-4 flex-shrink-0 text-gray-400">—</span>
                          )}
                          <Icon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          <span className={`text-sm ${included ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                            {feature.label}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Expires On */}
              <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Expires On</h4>
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="h-5 w-5 text-gray-500" />
                  <p className="text-lg font-medium text-gray-900">
                    {formatDate(subscriptionDetails.end_date)}
                  </p>
                </div>
                <p className="text-sm text-gray-600">
                  {subscriptionDetails.days_remaining != null && subscriptionDetails.days_remaining > 0
                    ? `${subscriptionDetails.days_remaining} days remaining`
                    : 'Expired'}
                </p>
              </div>

              {/* Additional Details */}
              <div className="pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-900">Started:</span>
                    <p className="text-sm text-gray-600 mt-1">{formatDate(subscriptionDetails.start_date)}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">Auto Renewal:</span>
                    <p className="text-sm text-gray-600 mt-1">
                      {subscriptionDetails.auto_renew ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                  {subscriptionDetails.is_in_trial && (
                    <div className="sm:col-span-2">
                      <span className="text-sm font-medium text-gray-900">Trial Ends:</span>
                      <p className="text-sm text-gray-600 mt-1">{formatDate(subscriptionDetails.trial_ends_at)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDetailsModal;

